import { darkModeSupported } from 'virtual:pwa-theme-meta';

import config from '@/config';
import { readCognitoAccount } from '@shared/auth';
import type { Config } from '@shared/config';
import { preferredLanguageOptions } from '@shared/lib/preferred-language';
import type { MessageKey } from '@shared/i18n';
import type { SettingsGroup } from './settings-search';

// Labels are message keys, translated when the dialog renders
// (`localizeSettings`), so the registry stays plain data.
const k = <K extends MessageKey>(key: K) => key;

/**
 * Everything the Settings dialog offers, in nav order. To add a setting:
 * add an entry here, a control for its id in `ui/setting-controls.tsx`,
 * and, if not every tenant has it, a rule in `isSettingAvailable`. To add
 * a page or a group, add it here and give the page an icon in
 * `ui/settings-dialog.tsx`'s `PAGE_ICONS`. Page ids are URL values
 * (`?settings=reading`) -- renaming one breaks links people have shared.
 *
 * `layout: 'stacked'` puts the control under the label, full width, for
 * lists and blocks of text rather than a single control.
 */
export const SETTINGS_GROUPS: ReadonlyArray<SettingsGroup<MessageKey>> = [
	{
		id: 'settings',
		label: k('settings.group.settings'),
		pages: [
			{
				id: 'general',
				label: k('settings.page.general'),
				sections: [
					{
						id: 'appearance',
						title: k('settings.section.appearance'),
						settings: [
							{
								id: 'theme',
								label: k('settings.theme.label'),
								description: k('settings.theme.description'),
								keywords: ['dark', 'light', 'mode', 'color', 'colour', 'night', 'system'],
							},
							{
								id: 'motion',
								label: k('settings.motion.label'),
								description: k('settings.motion.description'),
								keywords: ['animation', 'reduce', 'transition', 'accessibility', 'system'],
							},
						],
					},
					{
						id: 'languages',
						title: k('settings.section.languages'),
						settings: [
							{
								id: 'language',
								label: k('settings.language.label'),
								description: k('settings.language.description'),
								keywords: ['language', 'locale', 'translation', 'translate', 'preferred'],
							},
						],
					},
				],
			},
			{
				id: 'account',
				label: k('settings.page.account'),
				sections: [
					{
						id: 'account',
						title: k('settings.section.account'),
						settings: [
							{
								id: 'account',
								label: k('settings.account.label'),
								keywords: ['sign in', 'sign out', 'log in', 'log out', 'login', 'logout', 'sign up', 'profile'],
							},
							{
								id: 'subscription',
								label: k('settings.subscription.label'),
								description: k('settings.subscription.description'),
								keywords: ['billing', 'payment', 'subscribe', 'plan', 'cancel'],
							},
						],
					},
				],
			},
			{
				id: 'privacy',
				label: k('settings.page.privacy'),
				sections: [
					{
						id: 'analytics',
						title: k('settings.section.analytics'),
						settings: [
							{
								id: 'analytics',
								label: k('settings.analytics.label'),
								description: k('settings.analytics.description'),
								keywords: ['tracking', 'cookies', 'consent', 'gdpr', 'privacy', 'data'],
							},
						],
					},
					{
						id: 'reading-stats',
						title: k('settings.section.readingStats'),
						settings: [
							{
								id: 'reading-stats',
								label: k('settings.readingStats.label'),
								description: k('settings.readingStats.description'),
								keywords: ['stats', 'statistics', 'streak', 'history', 'time', 'privacy'],
							},
							{
								id: 'clear-reading-stats',
								label: k('settings.clearReadingStats.label'),
								description: k('settings.clearReadingStats.description'),
								keywords: ['stats', 'statistics', 'reset', 'clear', 'delete', 'history'],
							},
						],
					},
				],
			},
			{
				id: 'accessibility',
				label: k('settings.page.accessibility'),
				sections: [
					{
						id: 'links',
						title: k('settings.section.links'),
						settings: [
							{
								id: 'underline-links',
								label: k('settings.underlineLinks.label'),
								description: k('settings.underlineLinks.description'),
								keywords: ['underline', 'links', 'contrast'],
							},
						],
					},
					{
						id: 'keyboard',
						title: k('settings.section.keyboard'),
						settings: [
							{
								id: 'keyboard-shortcuts',
								label: k('settings.keyboardShortcuts.label'),
								layout: 'stacked',
								keywords: ['hotkeys', 'keys', 'keyboard'],
							},
						],
					},
				],
			},
			{
				id: 'storage',
				label: k('settings.page.storage'),
				sections: [
					{
						id: 'storage',
						title: k('settings.section.storage'),
						settings: [
							{
								id: 'storage-usage',
								label: k('settings.storageUsage.label'),
								description: k('settings.storageUsage.description'),
								layout: 'stacked',
								keywords: ['space', 'disk', 'quota', 'persistent', 'offline'],
							},
						],
					},
					{
						id: 'downloads',
						title: k('settings.section.downloads'),
						settings: [
							{
								id: 'downloads',
								label: k('settings.downloads.label'),
								description: k('settings.downloads.description'),
								layout: 'stacked',
								keywords: ['offline', 'saved', 'download', 'remove', 'delete'],
							},
						],
					},
					{
						id: 'cache',
						title: k('settings.section.cache'),
						settings: [
							{
								id: 'clear-cache',
								label: k('settings.clearCache.label'),
								description: k('settings.clearCache.description'),
								keywords: ['reset', 'clear', 'cache', 'data', 'delete'],
							},
						],
					},
				],
			},
			{
				id: 'about',
				label: k('settings.page.about'),
				sections: [
					{
						id: 'app',
						title: k('settings.section.app'),
						settings: [
							{
								id: 'app-version',
								label: k('settings.appVersion.label'),
								keywords: ['version', 'build', 'release'],
							},
							{
								id: 'check-updates',
								label: k('settings.checkUpdates.label'),
								description: k('settings.checkUpdates.description'),
								keywords: ['update', 'upgrade', 'new version', 'refresh'],
							},
							{
								id: 'install-app',
								label: k('settings.installApp.label'),
								description: k('settings.installApp.description'),
								keywords: ['install', 'home screen', 'pwa', 'desktop', 'add'],
							},
						],
					},
					{
						id: 'legal',
						title: k('settings.section.legal'),
						settings: [
							{
								id: 'legal-links',
								label: k('settings.legalLinks.label'),
								layout: 'stacked',
								keywords: ['terms', 'privacy policy', 'cookies', 'legal'],
							},
						],
					},
					{
						id: 'support',
						title: k('settings.section.support'),
						settings: [
							{
								id: 'debug-info',
								label: k('settings.debugInfo.label'),
								description: k('settings.debugInfo.description'),
								keywords: ['debug', 'support', 'logs', 'problem', 'bug', 'help'],
							},
						],
					},
				],
			},
		],
	},
	{
		id: 'reader',
		label: k('settings.group.reader'),
		pages: [
			{
				id: 'reading',
				label: k('settings.page.reading'),
				sections: [
					{
						id: 'text',
						title: k('settings.section.text'),
						settings: [
							{
								id: 'text-size',
								label: k('settings.textSize.label'),
								description: k('settings.textSize.description'),
								keywords: ['font', 'zoom', 'bigger', 'smaller', 'larger', 'scale'],
							},
							{
								id: 'line-spacing',
								label: k('settings.lineSpacing.label'),
								description: k('settings.lineSpacing.description'),
								keywords: ['line height', 'leading', 'spacing'],
							},
							{
								id: 'article-font',
								label: k('settings.articleFont.label'),
								description: k('settings.articleFont.description'),
								keywords: ['typeface', 'serif', 'sans', 'font family'],
							},
							{
								id: 'text-align',
								label: k('settings.textAlign.label'),
								description: k('settings.textAlign.description'),
								keywords: ['alignment', 'justify', 'ragged', 'left', 'spacing'],
							},
						],
					},
					{
						id: 'layout',
						title: k('settings.section.layout'),
						settings: [
							{
								id: 'reading-mode',
								label: k('settings.readingMode.label'),
								description: k('settings.readingMode.description'),
								keywords: ['replica', 'responsive', 'print', 'pdf', 'view', 'mode'],
							},
							{
								id: 'page-layout',
								label: k('settings.pageLayout.label'),
								description: k('settings.pageLayout.description'),
								keywords: ['spread', 'double', 'single', 'replica', 'pages'],
							},
						],
					},
					{
						id: 'article-colors',
						title: k('settings.section.articleColors'),
						settings: [
							{
								id: 'dark-articles',
								label: k('settings.darkArticles.label'),
								description: k('settings.darkArticles.description'),
								keywords: ['dark', 'night', 'invert', 'colour', 'color', 'black'],
							},
						],
					},
				],
			},
			{
				id: 'audio',
				label: k('settings.page.audio'),
				sections: [
					{
						id: 'playback',
						title: k('settings.section.playback'),
						settings: [
							{
								id: 'playback-speed',
								label: k('settings.playbackSpeed.label'),
								description: k('settings.playbackSpeed.description'),
								keywords: ['speed', 'rate', 'listen', 'audio', 'faster', 'slower'],
							},
						],
					},
				],
			},
			{
				// The reader's own figures across everything they have read:
				// about reading, not a setting, so it sits with the reader's pages,
				// after the two that do set something.
				id: 'stats',
				label: k('stats.title'),
				sections: [
					{
						// Charts by hour or by day, first on the page; choosing a
						// column shows its figures. The section's title is the
						// control's name, so the row shows no label of its own.
						id: 'stats-activity',
						title: k('stats.activity'),
						settings: [
							{
								id: 'stats-daily',
								label: k('stats.activity'),
								hideLabel: true,
								layout: 'stacked',
								keywords: ['stats', 'chart', 'graph', 'activity', 'today', 'hours', 'days', 'week', 'month', 'daily', 'history'],
							},
						],
					},
					{
						id: 'stats',
						title: k('stats.title'),
						hideTitle: true,
						settings: [
							{
								id: 'stats-overview',
								label: k('settings.stats.overview'),
								description: k('settings.readingStats.description'),
								layout: 'stacked',
								keywords: ['stats', 'statistics', 'streak', 'time', 'history', 'reading'],
							},
							{
								id: 'stats-recent',
								label: k('settings.section.statsRecent'),
								description: k('stats.recentDescription'),
								layout: 'stacked',
								keywords: ['stats', 'recent', 'issues', 'history', 'progress'],
							},
						],
					},
				],
			},
		],
	},
];

const hasLegalLinks = (tenant: Config) => {
	const legal = tenant?.app?.legal;
	return !!(legal?.tou?.url || legal?.privacy?.url || legal?.cookie?.url);
};

/**
 * Per-tenant availability. A setting with nothing to choose isn't shown:
 *
 * - Theme (and dark article pages) need a tenant whose accent survives the
 *   dark background (scripts/lib/theme/contrast.ts).
 * - Preferred language needs more than one language to choose from.
 * - Reading → Layout follows `issue.contentType`. `responsive` has no page
 *   view, so neither row shows and the section disappears; `replica` only
 *   offers Page layout (there's no text view to open in); only
 *   `responsive-replica` offers both.
 * - The rest follow the tenant's own feature switches.
 *
 * `tenant` defaults to the build's config; tests pass their own.
 */
export function isSettingAvailable(
	settingId: string,
	tenant: Config = config,
): boolean {
	const contentType = tenant?.issue?.contentType;
	switch (settingId) {
		case 'theme':
		case 'dark-articles':
			return darkModeSupported;
		case 'language':
			return (
				preferredLanguageOptions(
					tenant.build.language,
					tenant.build.languages,
				).length > 1
			);
		case 'account':
			return readCognitoAccount() !== null;
		case 'subscription':
			return !!readCognitoAccount()?.customerPortalUrl;
		case 'downloads':
			return !!tenant?.app?.downloadedIssues?.enabled;
		case 'reading-mode':
			return contentType === 'responsive-replica';
		case 'page-layout':
			return contentType === 'replica' || contentType === 'responsive-replica';
		case 'legal-links':
			return hasLegalLinks(tenant);
		default:
			return true;
	}
}
